import { isOpenInboxIssue } from "../lib/inboxIssueStatus"
import { useMemo, useRef, useState } from "react"
import { constellationLayout } from "./ConceptConstellation"
import { orderedInboxIssues } from "./Inbox"

const INITIAL_RAIL_ITEM_COUNT = 5
const RAIL_ITEM_BATCH_SIZE = 10

export function nextRailVisibleCount(currentCount, totalCount) {
  const current = Math.max(INITIAL_RAIL_ITEM_COUNT, Number(currentCount) || 0)
  const total = Math.max(0, Number(totalCount) || 0)
  return Math.min(total, current + RAIL_ITEM_BATCH_SIZE)
}

function railPagination(items, pagination) {
  const signature = items.map((item) => item?.key || item?.id).join("|")
  const count = pagination.signature === signature ? pagination.count : INITIAL_RAIL_ITEM_COUNT

  return { count: Math.min(count, items.length), signature }
}

export function issueRailInteractionTarget({ focused = null, hovered = null } = {}) {
  return hovered || focused || null
}

// The sidebar's conversation-list of open inbox issues. Selection only:
// status changes stay in the list rows and the floating detail panel. Rows
// are plain titles — no severity marks — so the column reads as one calm
// list against the sky. `variant="mobile"` renders the same list inside the
// mobile workspace menu.
export function IssueRail({ issues = [], onHover = null, onSelect, sectionLabel = null, selectedIssueKey = null, variant = "sidebar" }) {
  const interactionRef = useRef({ focused: null, hovered: null })
  const [pagination, setPagination] = useState({ signature: null, count: INITIAL_RAIL_ITEM_COUNT })
  const openIssues = orderedInboxIssues(issues).filter((issue) => isOpenInboxIssue(issue))
  const page = sectionLabel
    ? railPagination(openIssues, pagination)
    : { count: openIssues.length, signature: null }
  const visibleIssues = openIssues.slice(0, page.count)

  if (openIssues.length === 0 && !sectionLabel) return null

  function updateInteraction(kind, issue) {
    interactionRef.current[kind] = issue
    onHover?.(issueRailInteractionTarget(interactionRef.current))
  }

  return (
    <nav className={`issue-rail is-${variant}`} aria-label="Inbox issues">
      {sectionLabel ? <h2 className="rail-section-label">{sectionLabel}</h2> : null}
      {visibleIssues.map((issue) => {
        const selected = issue.key === selectedIssueKey || String(issue.id) === String(selectedIssueKey)

        return (
          <a
            key={issue.id ?? issue.key}
            aria-current={selected ? "page" : undefined}
            className={`issue-rail-item${selected ? " is-selected" : ""}`}
            data-sidebar-label={issue.title}
            href={issue.detailPath || "#"}
            onClick={(event) => onSelect?.(issue, event)}
            onFocus={() => updateInteraction("focused", issue)}
            onBlur={() => updateInteraction("focused", null)}
            onMouseEnter={() => updateInteraction("hovered", issue)}
            onMouseLeave={() => updateInteraction("hovered", null)}
          >
            <span className="issue-rail-title">{issue.title}</span>
          </a>
        )
      })}
      {page.count < openIssues.length ? (
        <button
          type="button"
          className="rail-see-more"
          onClick={() => setPagination({
            signature: page.signature,
            count: nextRailVisibleCount(page.count, openIssues.length)
          })}
        >
          See more &gt;
        </button>
      ) : null}
    </nav>
  )
}

function ConceptRailSigil({ concept }) {
  const layout = useMemo(() => constellationLayout(concept, {
    width: 240,
    height: 160,
    maxBodies: 6
  }), [concept])
  const satelliteByKey = new Map(layout.satellites.map((satellite) => [satellite.key, satellite]))
  const point = (node) => ({
    x: (node.x / layout.width) * 96,
    y: (node.y / layout.height) * 64
  })
  const core = point(layout.core)

  return (
    <svg
      aria-hidden="true"
      className="concept-rail-sigil"
      data-concept-key={concept.key}
      viewBox="0 0 96 64"
    >
      {layout.satellites.map((satellite) => {
        const parent = satellite.parentKey ? satelliteByKey.get(satellite.parentKey) : null
        const from = point(parent || layout.core)
        const to = point(satellite)

        return <line key={`edge-${satellite.key}`} x1={from.x} y1={from.y} x2={to.x} y2={to.y} />
      })}
      <circle className="is-core" cx={core.x} cy={core.y} r="4.8" />
      {layout.satellites.map((satellite) => {
        const satellitePoint = point(satellite)
        return <circle key={satellite.key} cx={satellitePoint.x} cy={satellitePoint.y} r="3.6" />
      })}
    </svg>
  )
}

export function ConceptRail({ concepts = [], onSelect, selectedConceptKey = null, showSectionLabel = true }) {
  const [pagination, setPagination] = useState({ signature: null, count: INITIAL_RAIL_ITEM_COUNT })
  const items = (Array.isArray(concepts) ? concepts : []).filter((concept) => concept?.key)
  const page = railPagination(items, pagination)
  const visibleItems = items.slice(0, page.count)
  if (items.length === 0) return null

  return (
    <nav className="concept-rail" aria-label="Concepts">
      {showSectionLabel ? <h2 className="rail-section-label">Concepts</h2> : null}
      {visibleItems.map((concept) => {
        const selected = concept.key === selectedConceptKey
        const title = concept.displayTitle || concept.title || concept.key

        return (
          <a
            key={concept.key}
            aria-current={selected ? "page" : undefined}
            className={`concept-rail-item${selected ? " is-selected" : ""}`}
            data-sidebar-label={title}
            href={concept.detailPath || "#"}
            onClick={(event) => onSelect?.(concept, event)}
          >
            <ConceptRailSigil concept={concept} />
            <span className="concept-rail-title">{title}</span>
          </a>
        )
      })}
      {page.count < items.length ? (
        <button
          type="button"
          className="rail-see-more"
          onClick={() => setPagination({
            signature: page.signature,
            count: nextRailVisibleCount(page.count, items.length)
          })}
        >
          See more &gt;
        </button>
      ) : null}
    </nav>
  )
}
